import React, { FunctionComponent, useRef, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { styled } from "@iventis/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const MAX_TITLE_LENGTH = 30;
const OVERLAY_WIDTH_RATIO = 0.3; // Default overlay width vs QR size
const OVERLAY_PADDING_RATIO = 0.06; // Internal padding vs overlay width
const MIN_FONT_SIZE = 10; // Prevent overly small text
const MAX_OVERLAY_AREA_RATIO = 0.18; // Overlay area capped vs QR area for scanability
const MIN_OVERLAY_WIDTH_RATIO = 0.2; // Do not shrink overlay below 20% of QR width
const LOGO_HEIGHT_CAP_RATIO = 0.55; // Cap logo height vs overlay width
const BORDER_PADDING_RATIO = 0.06; // Outer border padding vs QR size
const MIN_BORDER_PX = 12; // Minimum border thickness in pixels

export interface QRCodeDisplayProps {
    value: string;
    size?: number;
    level?: "L" | "M" | "Q" | "H";
    title?: string;
    expiryDate?: string | Date;
    logoUrl?: string;
    className?: string;
    "data-testid"?: string;
    onCopyCompleted?: () => void;
    onCopyError?: (error: Error) => void;
    onDownloadCompleted?: () => void;
    onDownloadError?: (error: Error) => void;
}

export const QRCodeDisplay: FunctionComponent<QRCodeDisplayProps> = ({
    value,
    size = 200,
    level = "H", // High error correction improves scanability with center overlay
    title,
    expiryDate,
    logoUrl,
    className,
    "data-testid": testId,
    onCopyCompleted,
    onCopyError,
    onDownloadCompleted,
    onDownloadError,
}) => {
    const qrRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [qrWithLogo, setQrWithLogo] = useState<string | null>(null);

    // Truncate title for display
    const truncatedTitle = title ? (title.length > MAX_TITLE_LENGTH ? `${title.substring(0, MAX_TITLE_LENGTH)}...` : title) : null;

    useEffect(() => {
        if (!value || !qrRef.current) return;

        const generateQRWithLogo = async () => {
            const svgElement = qrRef.current.querySelector("svg");
            if (!svgElement) return;

            const canvas = canvasRef.current;
            if (!canvas) return;

            const ctx = canvas.getContext("2d");
            if (!ctx) return; // Context not available

            const svgData = new XMLSerializer().serializeToString(svgElement);
            const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(svgBlob);

            const img = new Image();
            img.onload = () => {
                // Compute outer border padding and total canvas size
                const border = Math.max(MIN_BORDER_PX, Math.round(size * BORDER_PADDING_RATIO));
                const totalW = size + border * 2;
                canvas.width = totalW;
                canvas.height = totalW;

                // Paint white background (border area) then draw QR code
                ctx.fillStyle = "white";
                ctx.fillRect(0, 0, totalW, totalW);
                ctx.drawImage(img, border, border, size, size);

                // Utility: draw rounded rectangle overlay with optional logo and title
                const drawCenterOverlay = (logo?: HTMLImageElement) => {
                    // Helper to compute dimensions for given overlay width
                    const computeDims = (overlayW: number) => {
                        const pad = Math.max(4, Math.round(overlayW * OVERLAY_PADDING_RATIO));
                        // Logo sizing within overlay (contain)
                        const maxLogoW = overlayW - pad * 2;
                        let logoDrawW = 0;
                        let logoDrawH = 0;
                        if (logo) {
                            const iw = logo.naturalWidth || logo.width;
                            const ih = logo.naturalHeight || logo.height;
                            if (iw > 0 && ih > 0 && maxLogoW > 0) {
                                const scale = Math.min(maxLogoW / iw, maxLogoW / ih);
                                logoDrawW = Math.round(iw * scale);
                                logoDrawH = Math.round(ih * scale);
                            } else {
                                logoDrawW = Math.max(0, Math.round(maxLogoW));
                                logoDrawH = Math.max(0, Math.round(maxLogoW));
                            }
                            // Cap logo height to a fraction of overlay width
                            const maxLogoHCap = Math.round(overlayW * LOGO_HEIGHT_CAP_RATIO);
                            if (logoDrawH > maxLogoHCap && logoDrawH > 0 && logoDrawW > 0) {
                                const ratio = maxLogoHCap / logoDrawH;
                                logoDrawH = Math.round(logoDrawH * ratio);
                                logoDrawW = Math.round(logoDrawW * ratio);
                            }
                        }

                        // Compute text sizing to fit overlay width
                        const label = truncatedTitle || "";
                        let fontSize = Math.round(overlayW * 0.14);
                        ctx.textAlign = "center";
                        ctx.textBaseline = "middle";
                        ctx.fillStyle = "black";
                        if (label) {
                            while (fontSize > MIN_FONT_SIZE) {
                                ctx.font = `500 ${fontSize}px Arial, Helvetica, sans-serif`;
                                const textW = ctx.measureText(label).width;
                                if (textW <= overlayW - pad * 2) break;
                                fontSize -= 1;
                            }
                        }
                        const lineH = label ? Math.round(fontSize * 1.2) : 0;
                        const overlayH = pad + (logo ? logoDrawH : 0) + (label ? (logo ? Math.round(pad * 0.75) : 0) + lineH : 0) + pad;
                        return { overlayW, pad, logoDrawW, logoDrawH, fontSize, lineH, label, overlayH };
                    };

                    // Initial dimensions based on default width ratio
                    let dims = computeDims(Math.round(size * OVERLAY_WIDTH_RATIO));
                    const maxArea = Math.round(MAX_OVERLAY_AREA_RATIO * size * size);
                    const minW = Math.round(size * MIN_OVERLAY_WIDTH_RATIO);
                    // If overlay area exceeds max ratio, scale down
                    if (dims.overlayW * dims.overlayH > maxArea && dims.overlayW > minW) {
                        const scale = Math.sqrt(maxArea / (dims.overlayW * dims.overlayH));
                        const newW = Math.max(minW, Math.round(dims.overlayW * scale));
                        dims = computeDims(newW);
                    }

                    const { overlayW, overlayH, pad, logoDrawW, logoDrawH, fontSize, lineH, label } = dims;
                    const overlayX = Math.round(border + (size - overlayW) / 2);
                    const overlayY = Math.round(border + (size - overlayH) / 2);
                    const r = Math.round(Math.min(12, overlayW * 0.08));
                    // Draw rounded rectangle background centered
                    ctx.fillStyle = "white";
                    ctx.beginPath();
                    ctx.moveTo(overlayX + r, overlayY);
                    ctx.lineTo(overlayX + overlayW - r, overlayY);
                    ctx.quadraticCurveTo(overlayX + overlayW, overlayY, overlayX + overlayW, overlayY + r);
                    ctx.lineTo(overlayX + overlayW, overlayY + overlayH - r);
                    ctx.quadraticCurveTo(overlayX + overlayW, overlayY + overlayH, overlayX + overlayW - r, overlayY + overlayH);
                    ctx.lineTo(overlayX + r, overlayY + overlayH);
                    ctx.quadraticCurveTo(overlayX, overlayY + overlayH, overlayX, overlayY + overlayH - r);
                    ctx.lineTo(overlayX, overlayY + r);
                    ctx.quadraticCurveTo(overlayX, overlayY, overlayX + r, overlayY);
                    ctx.closePath();
                    ctx.fill();

                    // Draw logo centered at top inside overlay
                    if (logo && logoDrawW > 0 && logoDrawH > 0) {
                        const logoX = Math.round(overlayX + (overlayW - logoDrawW) / 2);
                        const logoY = Math.round(overlayY + pad);
                        ctx.drawImage(logo, logoX, logoY, logoDrawW, logoDrawH);
                    }

                    // Draw title centered below logo
                    if (label) {
                        ctx.fillStyle = "black";
                        ctx.font = `500 ${fontSize}px Arial, Helvetica, sans-serif`;
                        const textX = Math.round(overlayX + overlayW / 2);
                        const textY = Math.round(overlayY + pad + (logo ? logoDrawH : 0) + (logo ? Math.round(pad * 0.75) : 0) + Math.round(lineH / 2));
                        ctx.fillText(label, textX, textY);
                    }
                };

                // Draw logo and title if provided
                if (logoUrl || truncatedTitle) {
                    const logoImg = new Image();
                    logoImg.crossOrigin = "anonymous";
                    logoImg.onload = () => {
                        drawCenterOverlay(logoUrl ? logoImg : undefined);
                        setQrWithLogo(canvas.toDataURL("image/png"));
                        URL.revokeObjectURL(url);
                    };
                    logoImg.onerror = () => {
                        // Draw overlay with title only if available
                        if (truncatedTitle) {
                            drawCenterOverlay(undefined);
                        }
                        setQrWithLogo(canvas.toDataURL("image/png"));
                        URL.revokeObjectURL(url);
                    };
                    if (logoUrl) {
                        logoImg.src = logoUrl;
                    } else {
                        // No logo, just draw title overlay
                        drawCenterOverlay(undefined);
                        setQrWithLogo(canvas.toDataURL("image/png"));
                        URL.revokeObjectURL(url);
                    }
                } else {
                    setQrWithLogo(canvas.toDataURL("image/png"));
                    URL.revokeObjectURL(url);
                }

                // Draw expiry date in bottom-right on the border area
                if (expiryDate) {
                    const formatDate = (val: string | Date) => {
                        try {
                            const d = typeof val === "string" ? new Date(val) : val;
                            if (Number.isNaN(d.getTime())) return String(val);
                            const y = d.getFullYear();
                            const m = `${d.getMonth() + 1}`.padStart(2, "0");
                            const dd = `${d.getDate()}`.padStart(2, "0");
                            return `${y}-${m}-${dd}`;
                        } catch {
                            return String(val);
                        }
                    };
                    const label = `Expires: ${formatDate(expiryDate)}`;
                    const textPad = Math.round(border * 0.25);
                    const fontSize = Math.max(MIN_FONT_SIZE, Math.round(border * 0.5));
                    ctx.font = `400 ${fontSize}px Arial, Helvetica, sans-serif`;
                    ctx.textAlign = "right";
                    ctx.textBaseline = "alphabetic";
                    ctx.fillStyle = "#111";
                    ctx.fillText(label, totalW - textPad, totalW - textPad);
                }
            };
            img.onerror = () => {
                // Clean up URL on image load failure
                URL.revokeObjectURL(url);
            };
            img.src = url;
        };

        generateQRWithLogo();
    }, [value, logoUrl, size]);

    const handleCopy = async () => {
        if (!qrWithLogo) return;

        try {
            const blob = await fetch(qrWithLogo).then((res) => res.blob());
            const { ClipboardItem: ClipboardItemCtor } = (window as unknown) as { ClipboardItem?: new (items: Record<string, Blob>) => unknown };
            const { clipboard } = navigator as Navigator & { clipboard?: { write?: (data: unknown[]) => Promise<void> } };
            if (clipboard?.write && ClipboardItemCtor) {
                await clipboard.write([new ClipboardItemCtor({ [blob.type]: blob })]);
                onCopyCompleted?.();
            } else {
                // Fallback: trigger download
                const link = document.createElement("a");
                link.setAttribute("href", URL.createObjectURL(blob));
                link.setAttribute("download", "qr-code.png");
                link.click();
                onCopyCompleted?.();
            }
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            // eslint-disable-next-line no-console
            console.error("Failed to copy QR code to clipboard:", error);
            onCopyError?.(error);
        }
    };

    const handleDownload = () => {
        if (!qrWithLogo) return;

        try {
            // Sanitize filename: truncate to MAX_TITLE_LENGTH, remove special characters, and trim hyphens
            const sanitizedTitle = title
                ? title
                      .substring(0, MAX_TITLE_LENGTH)
                      .replace(/[^a-zA-Z0-9-_]/g, "-")
                      .replace(/-+/g, "-")
                      .replace(/^-+|-+$/g, "")
                : "download";
            const link = document.createElement("a");
            link.setAttribute("download", `qr-code-${sanitizedTitle}.png`);
            link.setAttribute("href", qrWithLogo);
            link.click();
            onDownloadCompleted?.();
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            // eslint-disable-next-line no-console
            console.error("Failed to download QR code:", error);
            onDownloadError?.(error);
        }
    };

    if (!value) {
        return null;
    }

    return (
        <QRCodeContainer className={className} data-testid={testId}>
            <QRCodeWrapper>
                <div ref={qrRef} style={{ display: qrWithLogo ? "none" : "block" }}>
                    <QRCode value={value} size={size} level={level} />
                </div>
                {qrWithLogo && <img src={qrWithLogo} alt="QR Code" style={{ width: "100%", height: "auto", maxWidth: size }} />}
                <canvas ref={canvasRef} style={{ display: "none" }} />
            </QRCodeWrapper>
            <ActionButtons>
                <ActionButton onClick={handleCopy} title="Copy QR code" data-testid="copy-qr-button" disabled={!qrWithLogo}>
                    <FontAwesomeIcon icon="copy" />
                    <span>Copy</span>
                </ActionButton>
                <ActionButton onClick={handleDownload} title="Download QR code" data-testid="download-qr-button" disabled={!qrWithLogo}>
                    <FontAwesomeIcon icon="download" />
                    <span>Download</span>
                </ActionButton>
            </ActionButtons>
        </QRCodeContainer>
    );
};

const QRCodeContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
`;

const QRCodeWrapper = styled.div`
    padding: 2px;
    background: white;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    /* Let parent control width; child visuals scale to fit */
    img,
    svg {
        width: 100%;
        height: auto;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 12px;
    margin-top: 8px;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: ${({ theme }) => theme.primaryColors.focus};
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.9;
    }

    &:active {
        opacity: 0.8;
    }

    svg {
        font-size: 14px;
    }
`;
